import React from "react";
import { Quote } from "lucide-react";

const TESTIMONIALS = [
  {
    quote:
      "Working with OnlyFacts helps our small team save hours each week so we can focus on our journalism. They're responsive to our needs and the dashboard they built us has helped streamline our weekly data newsletter.",
    name: "Charis Palmer",
    org: "The Energy",
    initials: "CP",
    variant: "light",
  },
  {
    quote:
      "The OnlyFacts team brought huge credibility to our content, and were critical in our strategy to create rich digital content that raised our domain authority. Their combination of skills is unique.",
    name: "Kate Haddad",
    org: "The a2 Milk Company",
    initials: "KH",
    variant: "blue",
  },
  {
    quote:
      "The team turned our data about patient outcomes into powerful messages that told a clear story to lay audiences, despite the complex and nuanced subject matter.",
    name: "Shirin Amirbeaggi",
    org: "Chris O'Brien Lifehouse",
    initials: "SA",
    variant: "light",
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="lg:col-span-6">
            <p className="overline mb-4">Client voices</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1]">
              Built with teams<br />
              <span className="italic font-serif">obsessed</span> about<br />
              getting it right.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Journalists, analysts and clinical leaders trust OnlyFacts to turn complex data into communications that hold up under scrutiny, build authority, and move audiences.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={t.name} {...t} index={i} /> 
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ quote, name, org, initials, variant, index }) {
  const isBlue = variant === "blue";
  return (
    <figure
      data-testid={`testimonial-card-${index}`}
      className={`group relative p-8 md:p-10 flex flex-col justify-between transition-all duration-300 border
        ${isBlue
          ? "bg-[#0052FF] text-white border-[#0052FF] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#0A0A0A]"
          : "bg-white text-[#0A0A0A] border-black/10 hover:border-[#0A0A0A] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_rgba(10,10,10,1)]"}
      `}
    >
      <div
        className={`absolute top-6 right-6 ${
          isBlue ? "text-[#FDE047]" : "text-[#0052FF]"
        }`}
        aria-hidden="true"
      >
        <Quote size={36} strokeWidth={1.5} />
      </div>

      <blockquote
        className={`font-display font-medium text-xl md:text-[1.4rem] leading-snug tracking-tight mb-8 pr-8 ${
          isBlue ? "text-white" : "text-[#0A0A0A]"
        }`}
      >
        <span
          className={`${isBlue ? "text-[#FDE047]" : "text-[#0052FF]"} font-display font-black text-3xl leading-none mr-0.5`}
          aria-hidden="true"
        >
          “
        </span>
        {quote}
        <span
          className={`${isBlue ? "text-[#FDE047]" : "text-[#0052FF]"} font-display font-black text-3xl leading-none ml-0.5`}
          aria-hidden="true"
        >
          ”
        </span>
      </blockquote>

      <figcaption className={`flex items-center gap-4 pt-5 border-t ${isBlue ? "border-white/25" : "border-black/10"}`}>
        <div
          className={`w-11 h-11 flex items-center justify-center font-display font-black text-sm shrink-0 ${
            isBlue ? "bg-[#FDE047] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"
          }`}
        >
          {initials}
        </div>
        <div className="leading-tight">
          <div
            className={`font-display font-bold text-base ${
              isBlue ? "text-white" : "text-[#0A0A0A]"
            }`}
          >
            {name}
          </div>
          <div
            className={`font-mono text-[0.7rem] uppercase tracking-wider mt-0.5 ${
              isBlue ? "text-white/80" : "text-[#6B7280]"
            }`}
          >
            {org}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}
